import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Package, Settings, RefreshCw, Search, Filter, Eye, Truck, DollarSign, BarChart3, AlertCircle, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp, Save, TestTube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Durum renkleri
const STATUS_COLORS = {
  'Yeni Sipariş': 'bg-blue-100 text-blue-800 border-blue-300',
  'Hazırlanıyor': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Faturalandı': 'bg-purple-100 text-purple-800 border-purple-300',
  'Kargoya Verildi': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Teslim Edildi': 'bg-green-100 text-green-800 border-green-300',
  'Teslim Edilemedi': 'bg-orange-100 text-orange-800 border-orange-300',
  'İptal Edildi': 'bg-red-100 text-red-800 border-red-300',
  'İade Edildi': 'bg-gray-100 text-gray-800 border-gray-300'
};

const STATUS_ICONS = {
  'Yeni Sipariş': Clock,
  'Hazırlanıyor': Package,
  'Faturalandı': DollarSign,
  'Kargoya Verildi': Truck,
  'Teslim Edildi': CheckCircle,
  'Teslim Edilemedi': AlertCircle,
  'İptal Edildi': XCircle,
  'İade Edildi': RefreshCw
};

export default function Trendyol() {
  const [activeTab, setActiveTab] = useState('siparisler');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Siparişler state
  const [siparisler, setSiparisler] = useState([]);
  const [siparisFiltre, setSiparisFiltre] = useState({
    durum: '',
    baslangic: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    bitis: new Date().toISOString().split('T')[0]
  });
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [istatistikler, setIstatistikler] = useState(null);
  
  // Ürünler state
  const [urunler, setUrunler] = useState([]);
  const [urunFiltre, setUrunFiltre] = useState({ barkod: '', onaylandi: '' });
  
  // İadeler state
  const [iadeler, setIadeler] = useState([]);
  const [iadeFiltre, setIadeFiltre] = useState({
    durum: '',
    baslangic: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    bitis: new Date().toISOString().split('T')[0]
  });
  const [iadeToplam, setIadeToplam] = useState(0);
  
  // Ayarlar state
  const [ayarlar, setAyarlar] = useState({
    supplierId: '',
    apiKey: '',
    apiSecret: ''
  });
  const [ayarlarKaydediliyor, setAyarlarKaydediliyor] = useState(false);
  const [baglantiDurumu, setBaglantiDurumu] = useState(null);
  
  // Sayfalama
  const [sayfa, setSayfa] = useState(0);
  const [toplam, setToplam] = useState(0);

  // Siparişleri getir
  const fetchSiparisler = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sirket: 'miba',
        sayfa: sayfa.toString(),
        limit: '20'
      });
      
      if (siparisFiltre.durum) params.append('durum', siparisFiltre.durum);
      if (siparisFiltre.baslangic) params.append('baslangic_tarihi', siparisFiltre.baslangic);
      if (siparisFiltre.bitis) params.append('bitis_tarihi', siparisFiltre.bitis);
      
      const response = await fetch(`${API_URL}/api/trendyol/siparisler?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setSiparisler(data.siparisler || []);
        setToplam(data.toplam || 0);
      } else {
        setError(data.message || 'Siparişler alınamadı');
      }
    } catch (err) {
      setError('Bağlantı hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [sayfa, siparisFiltre]);

  // İstatistikleri getir
  const fetchIstatistikler = useCallback(async () => {
    try {
      const params = new URLSearchParams({ sirket: 'miba' });
      if (siparisFiltre.baslangic) params.append('baslangic', siparisFiltre.baslangic);
      if (siparisFiltre.bitis) params.append('bitis', siparisFiltre.bitis);
      
      const response = await fetch(`${API_URL}/api/trendyol/istatistikler?${params}`);
      const data = await response.json();
      if (data.success) {
        setIstatistikler(data.istatistikler);
      }
    } catch (err) {
      console.error('İstatistik hatası:', err);
    }
  }, [siparisFiltre.baslangic, siparisFiltre.bitis]);

  // Ürünleri getir
  const fetchUrunler = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sirket: 'miba',
        sayfa: sayfa.toString(),
        limit: '20'
      });
      
      if (urunFiltre.barkod) params.append('barkod', urunFiltre.barkod);
      if (urunFiltre.onaylandi !== '') params.append('onaylandi', urunFiltre.onaylandi);
      
      const response = await fetch(`${API_URL}/api/trendyol/urunler?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setUrunler(data.urunler || []);
        setToplam(data.toplam || 0);
      } else {
        setError(data.message || 'Ürünler alınamadı');
      }
    } catch (err) {
      setError('Bağlantı hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [sayfa, urunFiltre]);

  // İadeleri getir
  const fetchIadeler = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sirket: 'miba',
        sayfa: '0',
        limit: '50'
      });
      
      if (iadeFiltre.durum) params.append('durum', iadeFiltre.durum);
      if (iadeFiltre.baslangic) params.append('baslangic_tarihi', iadeFiltre.baslangic);
      if (iadeFiltre.bitis) params.append('bitis_tarihi', iadeFiltre.bitis);
      
      const response = await fetch(`${API_URL}/api/trendyol/iadeler?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setIadeler(data.iadeler || []);
        setIadeToplam(data.toplam || 0);
        setError(null);
      } else {
        // Cloudflare engeli kontrolü
        const errorMsg = data.message || data.detail || '';
        if (errorMsg.includes('Cloudflare') || errorMsg.includes('DOCTYPE')) {
          setError('Trendyol İade API\'si şu an erişime kapalı. Lütfen daha sonra tekrar deneyin.');
        } else {
          setError(errorMsg || 'İadeler alınamadı');
        }
      }
    } catch (err) {
      setError('Bağlantı hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [iadeFiltre]);

  // Ayarları getir
  const fetchAyarlar = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/trendyol/ayarlar?sirket=miba`);
      const data = await response.json();
      if (data.supplierId) {
        setAyarlar({
          supplierId: data.supplierId || '',
          apiKey: data.apiKey || '',
          apiSecret: data.apiSecret || ''
        });
        setBaglantiDurumu('connected');
      }
    } catch (err) {
      console.error('Ayarlar alınamadı:', err);
    }
  }, []);

  // Ayarları kaydet
  const saveAyarlar = async () => {
    if (!ayarlar.supplierId || !ayarlar.apiKey || !ayarlar.apiSecret) {
      setError('Tüm alanları doldurun');
      return;
    }
    
    setAyarlarKaydediliyor(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/trendyol/ayarlar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sirket: 'miba',
          ...ayarlar
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBaglantiDurumu('connected');
        alert('Trendyol API ayarları kaydedildi!');
        fetchSiparisler();
        fetchIstatistikler();
      } else {
        setError(data.detail || data.message || 'Kaydetme hatası');
        setBaglantiDurumu('error');
      }
    } catch (err) {
      setError('Bağlantı hatası: ' + err.message);
      setBaglantiDurumu('error');
    } finally {
      setAyarlarKaydediliyor(false);
    }
  };

  // Bağlantı testi
  const testBaglanti = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/trendyol/test?sirket=miba`);
      const data = await response.json();
      
      if (data.success) {
        setBaglantiDurumu('connected');
        alert('✅ Trendyol bağlantısı başarılı!');
      } else {
        setBaglantiDurumu('error');
        alert('❌ Bağlantı hatası: ' + data.message);
      }
    } catch (err) {
      setBaglantiDurumu('error');
      alert('❌ Bağlantı hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Tab değişiminde veri çek
  useEffect(() => {
    if (activeTab === 'siparisler') {
      fetchSiparisler();
      fetchIstatistikler();
    } else if (activeTab === 'urunler') {
      fetchUrunler();
    } else if (activeTab === 'iadeler') {
      fetchIadeler();
    } else if (activeTab === 'ayarlar') {
      fetchAyarlar();
    }
  }, [activeTab, fetchSiparisler, fetchUrunler, fetchAyarlar, fetchIstatistikler, fetchIadeler]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Trendyol</h1>
              <p className="text-sm text-orange-100">Sipariş ve Ürün Yönetimi</p>
            </div>
          </div>
          
          {baglantiDurumu === 'connected' && (
            <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Bağlı</span>
            </div>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mt-4 overflow-x-auto">
          {[
            { id: 'siparisler', label: 'Siparişler', icon: Package },
            { id: 'urunler', label: 'Ürünler', icon: ShoppingBag },
            { id: 'iadeler', label: 'İadeler', icon: RefreshCw },
            { id: 'ayarlar', label: 'Ayarlar', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white text-orange-600'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* SİPARİŞLER TAB */}
        {activeTab === 'siparisler' && (
          <div className="space-y-4">
            {/* İstatistikler */}
            {istatistikler && (
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{istatistikler.toplam}</div>
                  <div className="text-xs text-blue-700">Toplam</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{istatistikler.bekleyen}</div>
                  <div className="text-xs text-yellow-700">Bekleyen</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-purple-600">{istatistikler.hazirlanan}</div>
                  <div className="text-xs text-purple-700">Hazırlanan</div>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-600">{istatistikler.kargoda}</div>
                  <div className="text-xs text-indigo-700">Kargoda</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{istatistikler.teslim_edildi}</div>
                  <div className="text-xs text-green-700">Teslim</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{istatistikler.iptal + (istatistikler.iade || 0)}</div>
                  <div className="text-xs text-red-700">İptal/İade</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {(istatistikler.toplam_tutar || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                  </div>
                  <div className="text-xs text-orange-700">Toplam Tutar</div>
                </div>
              </div>
            )}

            {/* Filtreler */}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Durum</label>
                  <select
                    value={siparisFiltre.durum}
                    onChange={(e) => setSiparisFiltre(f => ({ ...f, durum: e.target.value }))}
                    className="border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Tümü</option>
                    <option value="Created">Yeni Sipariş</option>
                    <option value="Picking">Hazırlanıyor</option>
                    <option value="Shipped">Kargoda</option>
                    <option value="Delivered">Teslim Edildi</option>
                    <option value="Cancelled">İptal</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Başlangıç</label>
                  <Input
                    type="date"
                    value={siparisFiltre.baslangic}
                    onChange={(e) => setSiparisFiltre(f => ({ ...f, baslangic: e.target.value }))}
                    className="w-40"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Bitiş</label>
                  <Input
                    type="date"
                    value={siparisFiltre.bitis}
                    onChange={(e) => setSiparisFiltre(f => ({ ...f, bitis: e.target.value }))}
                    className="w-40"
                  />
                </div>
                <Button onClick={() => { fetchSiparisler(); fetchIstatistikler(); }} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Getir
                </Button>
              </div>
            </div>

            {/* Sipariş Listesi */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left">Sipariş No</th>
                      <th className="px-4 py-3 text-left">Tarih</th>
                      <th className="px-4 py-3 text-left">Müşteri</th>
                      <th className="px-4 py-3 text-left">Durum</th>
                      <th className="px-4 py-3 text-right">Tutar</th>
                      <th className="px-4 py-3 text-center">Ürün</th>
                      <th className="px-4 py-3 text-center">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siparisler.map((siparis, idx) => {
                      const StatusIcon = STATUS_ICONS[siparis.durum] || Clock;
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{siparis.siparisNo}</td>
                          <td className="px-4 py-3 text-xs">
                            {siparis.siparisTarihi}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium">{siparis.musteriAdi}</div>
                            <div className="text-xs text-gray-500">{siparis.il} / {siparis.ilce}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${STATUS_COLORS[siparis.durum] || 'bg-gray-100'}`}>
                              <StatusIcon className="w-3 h-3" />
                              {siparis.durum}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {siparis.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                          </td>
                          <td className="px-4 py-3 text-center">{siparis.urunSayisi}</td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedSiparis(siparis)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {siparisler.length === 0 && !loading && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          {baglantiDurumu !== 'connected' 
                            ? 'Önce Ayarlar sekmesinden API bilgilerini girin'
                            : 'Sipariş bulunamadı'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Sayfalama */}
              {toplam > 20 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <span className="text-sm text-gray-500">
                    Toplam {toplam} sipariş
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSayfa(s => Math.max(0, s - 1))}
                      disabled={sayfa === 0}
                    >
                      Önceki
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSayfa(s => s + 1)}
                      disabled={(sayfa + 1) * 20 >= toplam}
                    >
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ÜRÜNLER TAB */}
        {activeTab === 'urunler' && (
          <div className="space-y-4">
            {/* Bilgi Notu */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-blue-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>Ürün listesi siparişlerden oluşturulmuştur. Satışı yapılan benzersiz ürünleri gösterir.</span>
            </div>

            {/* Filtreler */}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Barkod</label>
                  <Input
                    value={urunFiltre.barkod}
                    onChange={(e) => setUrunFiltre(f => ({ ...f, barkod: e.target.value }))}
                    placeholder="Barkod ara..."
                    className="w-48"
                  />
                </div>
                <Button onClick={fetchUrunler} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Getir
                </Button>
              </div>
            </div>

            {/* Ürün Listesi */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left">Barkod</th>
                      <th className="px-4 py-3 text-left">Ürün Adı</th>
                      <th className="px-4 py-3 text-right">Satış Fiyatı</th>
                      <th className="px-4 py-3 text-center">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {urunler.map((urun, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs">{urun.barkod}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium max-w-md">{urun.urunAdi}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {urun.satisFiyati?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3" />
                            Satışta
                          </span>
                        </td>
                      </tr>
                    ))}
                    {urunler.length === 0 && !loading && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                          {baglantiDurumu !== 'connected' 
                            ? 'Önce Ayarlar sekmesinden API bilgilerini girin'
                            : 'Ürün bulunamadı'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Toplam Sayı */}
              {toplam > 0 && (
                <div className="p-4 border-t bg-gray-50 text-sm text-gray-600">
                  Toplam <strong>{toplam}</strong> benzersiz ürün
                </div>
              )}
            </div>
          </div>
        )}

        {/* İADELER TAB */}
        {activeTab === 'iadeler' && (
          <div className="space-y-4">
            {/* Filtreler */}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Durum</label>
                  <select
                    value={iadeFiltre.durum}
                    onChange={(e) => setIadeFiltre(f => ({ ...f, durum: e.target.value }))}
                    className="border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Tümü</option>
                    <option value="Open">Açık</option>
                    <option value="Solved">Çözüldü</option>
                    <option value="Rejected">Reddedildi</option>
                    <option value="InProgress">İşlemde</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Başlangıç</label>
                  <Input
                    type="date"
                    value={iadeFiltre.baslangic}
                    onChange={(e) => setIadeFiltre(f => ({ ...f, baslangic: e.target.value }))}
                    className="w-40"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Bitiş</label>
                  <Input
                    type="date"
                    value={iadeFiltre.bitis}
                    onChange={(e) => setIadeFiltre(f => ({ ...f, bitis: e.target.value }))}
                    className="w-40"
                  />
                </div>
                <Button onClick={fetchIadeler} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Getir
                </Button>
              </div>
            </div>

            {/* İade Listesi */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left">Sipariş No</th>
                      <th className="px-4 py-3 text-left">Tarih</th>
                      <th className="px-4 py-3 text-left">Müşteri</th>
                      <th className="px-4 py-3 text-left">Ürün</th>
                      <th className="px-4 py-3 text-left">Sebep</th>
                      <th className="px-4 py-3 text-center">Durum</th>
                      <th className="px-4 py-3 text-right">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iadeler.map((iade, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs">{iade.siparisNo}</td>
                        <td className="px-4 py-3">{iade.tarih}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium">{iade.musteri?.ad}</div>
                        </td>
                        <td className="px-4 py-3">
                          {iade.urunler?.map((u, i) => (
                            <div key={i} className="text-xs">{u.urunAdi?.slice(0, 40)}...</div>
                          ))}
                        </td>
                        <td className="px-4 py-3 text-xs">{iade.sebepText || iade.sebep}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            iade.durum === 'Open' ? 'bg-yellow-100 text-yellow-700' :
                            iade.durum === 'Solved' ? 'bg-green-100 text-green-700' :
                            iade.durum === 'Rejected' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {iade.durumText}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {iade.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                        </td>
                      </tr>
                    ))}
                    {iadeler.length === 0 && !loading && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          {baglantiDurumu !== 'connected' 
                            ? 'Önce Ayarlar sekmesinden API bilgilerini girin'
                            : 'İade talebi bulunamadı'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Toplam */}
              {iadeToplam > 0 && (
                <div className="p-4 border-t bg-gray-50 text-sm text-gray-600">
                  Toplam <strong>{iadeToplam}</strong> iade talebi
                </div>
              )}
            </div>
          </div>
        )}

        {/* AYARLAR TAB */}
        {activeTab === 'ayarlar' && (
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-xl border p-6 space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <Settings className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="font-bold text-lg">Trendyol API Ayarları</h2>
                  <p className="text-sm text-gray-500">Satıcı panelinden API bilgilerinizi alın</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Satıcı ID (Supplier ID)
                  </label>
                  <Input
                    value={ayarlar.supplierId}
                    onChange={(e) => setAyarlar(a => ({ ...a, supplierId: e.target.value }))}
                    placeholder="703486"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    API Key
                  </label>
                  <Input
                    value={ayarlar.apiKey}
                    onChange={(e) => setAyarlar(a => ({ ...a, apiKey: e.target.value }))}
                    placeholder="taapbVOPLYgHYW2bDtr"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    API Secret
                  </label>
                  <Input
                    type="password"
                    value={ayarlar.apiSecret}
                    onChange={(e) => setAyarlar(a => ({ ...a, apiSecret: e.target.value }))}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={saveAyarlar}
                  disabled={ayarlarKaydediliyor}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  <Save className={`w-4 h-4 mr-2 ${ayarlarKaydediliyor ? 'animate-spin' : ''}`} />
                  {ayarlarKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button
                  onClick={testBaglanti}
                  disabled={loading}
                  variant="outline"
                >
                  <TestTube className="w-4 h-4 mr-2" />
                  Test
                </Button>
              </div>

              {baglantiDurumu === 'connected' && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span>Trendyol bağlantısı aktif</span>
                </div>
              )}
              
              {baglantiDurumu === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span>Bağlantı hatası - bilgileri kontrol edin</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sipariş Detay Modal */}
      {selectedSiparis && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-orange-500 text-white p-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold">Sipariş Detayı</h3>
                <p className="text-sm text-orange-100">{selectedSiparis.siparisNo}</p>
              </div>
              <button onClick={() => setSelectedSiparis(null)} className="p-1 hover:bg-white/20 rounded">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Müşteri Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Müşteri</h4>
                  <p className="text-sm">{selectedSiparis.musteriAdi}</p>
                  <p className="text-sm text-gray-500">{selectedSiparis.musteriTel}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Teslimat Adresi</h4>
                  <p className="text-sm">{selectedSiparis.teslimatAdresi}</p>
                  <p className="text-sm text-gray-500">{selectedSiparis.il} / {selectedSiparis.ilce}</p>
                </div>
              </div>

              {/* Ürünler */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Ürünler</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Ürün</th>
                        <th className="px-3 py-2 text-right">Adet</th>
                        <th className="px-3 py-2 text-right">Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSiparis.urunler?.map((urun, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2">
                            <div className="font-medium">{urun.urunAdi}</div>
                            <div className="text-xs text-gray-500">{urun.barkod}</div>
                          </td>
                          <td className="px-3 py-2 text-right">{urun.miktar}</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {urun.tutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={2} className="px-3 py-2 text-right font-medium">Toplam:</td>
                        <td className="px-3 py-2 text-right font-bold text-orange-600">
                          {selectedSiparis.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Kargo */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-8 h-8 text-gray-400" />
                <div>
                  <p className="font-medium">{selectedSiparis.kargoFirmasi || 'Kargo atanmadı'}</p>
                  <p className="text-sm text-gray-500">
                    {selectedSiparis.kargoTakipNo || 'Takip numarası yok'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
